/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IModuleProps, Heading } from '@msdyn365-commerce-modules/utilities';
import { Image, IImageSettings } from '@msdyn365-commerce/core';
import { IDefaContentTileData } from './defa-content-tile.data';
import { IDefaContentTileProps } from './defa-content-tile.props.autogenerated';

export interface IDefaContentTileViewProps extends IDefaContentTileProps<IDefaContentTileData> {
    baseClassName: string;
    contentTile: IModuleProps;
    backgroundImage?: JSX.Element | null;
    heading?: JSX.Element | null;
    content?: JSX.Element | null;
    actionLink?: JSX.Element | null;
}

/**
 *
 * DefaContentTile component
 * @extends {React.PureComponent<IDefaContentTileProps<IDefaContentTileData>>}
 */
class DefaContentTile extends React.PureComponent<IDefaContentTileProps<IDefaContentTileData>> {

    private readonly baseClassName: string = 'defa-content-tile';

    public render(): JSX.Element | null {
        const viewProps: IDefaContentTileViewProps = {
            ...this.props,
            baseClassName: this.baseClassName,
            contentTile: {
                className: this.baseClassName,
                moduleProps: this.props
            },
            backgroundImage: this._renderBackgroundImage(),
            heading: this._renderHeading(),
            content: this._renderContent(),
            actionLink: this._renderActionLink()
        };

        return this.props.renderView(viewProps);
    }

    private _renderBackgroundImage(): JSX.Element | null {
        const { config, context } = this.props;

        if (!config.image) {
            return null;
        }

        const { image } = config;
        const Width = 800;
        const Height = 500;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { w: Width, h: Height, q: `w=${Width}&h=${Height}&q=80&m=0&f=webp`, qe: `t=${Width}h${Height}qhigh` },
                lg: { w: Width, h: Height, q: `w=${Width}&h=${Height}&q=80&m=0&f=webp`, qe: `t=${Width}h${Height}qhigh` },
                xl: { w: Width, h: Height, q: `w=${Width}&h=${Height}&q=80&m=0&f=webp`, qe: `t=${Width}h${Height}qhigh` }
            },
            disableLazyLoad: false,
            lazyload: false,
            quality: 80
        };

        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className={`${this.baseClassName}__background-image`}
                {...image}
                gridSettings={context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='default'
                bypassHideOnFailure={true}
            />
        );
    }

    private _renderHeading(): JSX.Element | null {
        const { config } = this.props;

        if (!config.heading) {
            return null;
        }

        return (
            <Heading className={`${this.baseClassName}__heading`} {...config.heading} />
        );
    }

    private _renderContent(): JSX.Element | null {
        const { config } = this.props;

        if (!config.content) {
            return null;
        }

        return (
            <div className={`${this.baseClassName}__content`}>{config.content}</div>
        );
    }

    private _renderActionLink(): JSX.Element | null {
        const { config } = this.props;

        if (!config.actionLink) {
            return null;
        }

        return (
            <a
                className={`${this.baseClassName}__action-link`}
                href={config.actionLink?.destinationUrl}
            >
                {config.actionLabel}
            </a>
        );
    }
}

export default DefaContentTile;
